import type { ProfileSummary } from './profile.api';

export const ACTIVE_PROFILE_UUID_STORAGE_KEY = 'active_profile_uuid';
export const LEGACY_ACTIVE_PROVIDER_UUID_STORAGE_KEY = 'active_provider_uuid';
export const ACCOUNT_PROFILES_STORAGE_KEY = 'account_profiles';
export const LEGACY_ACCOUNT_PROVIDERS_STORAGE_KEY = 'account_providers';

function readStorageValue(key: string): string | null {
  const rawValue = window.localStorage.getItem(key);
  if (!rawValue) {
    return null;
  }

  const normalizedValue = rawValue.trim();
  return normalizedValue.length > 0 ? normalizedValue : null;
}

export function readActiveProfileUuid(): string | null {
  return (
    readStorageValue(ACTIVE_PROFILE_UUID_STORAGE_KEY) ??
    readStorageValue(LEGACY_ACTIVE_PROVIDER_UUID_STORAGE_KEY)
  );
}

export function setActiveProfileUuid(profileUuid: string): void {
  window.localStorage.setItem(ACTIVE_PROFILE_UUID_STORAGE_KEY, profileUuid);
  window.localStorage.removeItem(LEGACY_ACTIVE_PROVIDER_UUID_STORAGE_KEY);
}

export function clearActiveProfileUuid(): void {
  window.localStorage.removeItem(ACTIVE_PROFILE_UUID_STORAGE_KEY);
  window.localStorage.removeItem(LEGACY_ACTIVE_PROVIDER_UUID_STORAGE_KEY);
}

function isProfileSummary(value: unknown): value is ProfileSummary {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const typedValue = value as Partial<ProfileSummary>;
  return typeof typedValue.uuid === 'string' && typeof typedValue.title === 'string';
}

function readStoredList(key: string): ProfileSummary[] {
  const rawValue = window.localStorage.getItem(key);
  if (!rawValue) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(rawValue) as unknown;
    if (!Array.isArray(parsedValue)) {
      return [];
    }

    return parsedValue.filter(isProfileSummary);
  } catch {
    return [];
  }
}

export function readStoredProfiles(): ProfileSummary[] {
  const storedProfiles = readStoredList(ACCOUNT_PROFILES_STORAGE_KEY);
  if (storedProfiles.length > 0) {
    return storedProfiles;
  }

  return readStoredList(LEGACY_ACCOUNT_PROVIDERS_STORAGE_KEY);
}

export function setStoredProfiles(profiles: ProfileSummary[]): void {
  window.localStorage.setItem(ACCOUNT_PROFILES_STORAGE_KEY, JSON.stringify(profiles));
  window.localStorage.removeItem(LEGACY_ACCOUNT_PROVIDERS_STORAGE_KEY);
}

export function clearStoredProfiles(): void {
  window.localStorage.removeItem(ACCOUNT_PROFILES_STORAGE_KEY);
  window.localStorage.removeItem(LEGACY_ACCOUNT_PROVIDERS_STORAGE_KEY);
}
